import type { Icon } from '@phosphor-icons/react'
import {
  Bug,
  CheckSquare,
  Sparkle,
  Wrench,
  Broom,
  Tray,
  TrendUp,
  Warning,
  Lightning,
  Star,
  Flag,
  Question,
  Lifebuoy,
  Bookmark,
  Target,
  Lightbulb,
  Rocket,
  Gear,
  Cube,
  Stack,
  ListChecks,
  Circle,
} from '@phosphor-icons/react'

import { cn } from '@/lib/utils'

/**
 * Icons available for task types. Keys are the stored `icon` strings on a
 * TaskTypeDef. Keep in sync with the shared TASK_TYPE_TEMPLATES icon names.
 */
export const TASK_TYPE_ICONS: Record<string, Icon> = {
  CheckSquare,
  Bug,
  Sparkle,
  Wrench,
  Broom,
  Tray,
  TrendUp,
  Warning,
  Lightning,
  Star,
  Flag,
  Question,
  Lifebuoy,
  Bookmark,
  Target,
  Lightbulb,
  Rocket,
  Gear,
  Cube,
  Stack,
  ListChecks,
  Circle,
}

/** Neutral fallback used for untyped tasks or unknown icon names. */
export const DEFAULT_TASK_TYPE_ICON = 'CheckSquare'

export function getTaskTypeIcon(iconName?: string | null): Icon {
  if (!iconName || !TASK_TYPE_ICONS[iconName]) {
    return TASK_TYPE_ICONS[DEFAULT_TASK_TYPE_ICON]
  }
  return TASK_TYPE_ICONS[iconName]
}

interface TaskTypeIconProps {
  icon?: string | null
  color?: string | null
  /** 'LETTER' renders a colored letter chip; 'ICON' (default) renders `icon`. */
  displayStyle?: string | null
  /** The type name — used to derive the letter in LETTER mode. */
  name?: string | null
  className?: string
  /** Accessible label (the type name); also used as the title tooltip. */
  label?: string
}

/** A colored chip showing a task type's initial, used in LETTER display mode. */
function LetterChip({
  letter,
  color,
  className,
  label,
}: {
  letter: string
  color?: string | null
  className?: string
  label?: string
}) {
  return (
    <span
      className={cn(
        'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] text-[10px] font-semibold leading-none text-white',
        className,
      )}
      style={{ backgroundColor: color || '#64748B' }}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      title={label}
    >
      {letter}
    </span>
  )
}

/**
 * Renders a task type's badge — either a colored letter chip (LETTER mode) or
 * its phosphor icon tinted with its color (ICON mode). Sized h-4 w-4 and
 * flex-shrink-0 so it slots in to the left of a task name without disturbing
 * existing row/card layouts. Untyped tasks (no displayStyle/name) fall back to
 * the neutral CheckSquare icon.
 */
export function TaskTypeIcon({
  icon,
  color,
  displayStyle,
  name,
  className,
  label,
}: TaskTypeIconProps) {
  const letterSource = name ?? label
  if (displayStyle === 'LETTER' && letterSource) {
    return (
      <LetterChip
        letter={(letterSource.trim()[0] ?? '?').toUpperCase()}
        color={color}
        className={className}
        label={label}
      />
    )
  }

  const IconComponent = getTaskTypeIcon(icon)
  return (
    <IconComponent
      className={cn('h-4 w-4 flex-shrink-0', className)}
      style={color ? { color } : undefined}
      weight="fill"
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {label ? <title>{label}</title> : null}
    </IconComponent>
  )
}
